/**
 * W1-11b (app-shell-ui): hash routes from `registry.routes()` (ARCHITECTURE.md §3.2; DESIGN.md §3.1).
 *
 * Patterns are hash paths with `:name` parameters (`#/p/:id/:workspace`, `#/settings/:section`); the pattern with
 * the most fixed segments wins, then the one declared first. The Library (`#/`) is W2-10's route; before it exists
 * the shell says so (Settings stay reachable). Unknown addresses show "Page not found" with a way back. Each page
 * has its own error boundary, reset when the address changes.
 *
 * W3-01 (UX-002): pages other than the editor (Library, Settings, dev pages) get the shell's notice row above them,
 * in the same place as under the editor's top bar, so module notices (the import queue, the latency hint) and the
 * storage warnings show up the same way everywhere. The editor route draws the row itself, under its top bar.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { EmptyState } from '../../ui/EmptyState';
import { hashSegments, normalizeHash } from '../navService';
import type { AppServices, RouteDef } from '../registry/types';
import { Banners } from './Banners';
import { ErrorBoundary } from './ErrorBoundary';
import { useOptionalShell } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

export interface RouteMatch {
  readonly route: RouteDef;
  readonly params: Readonly<Record<string, string>>;
}

/** The best route for a hash, with its parameters (decoded), or null. */
export function matchRoute(routes: readonly RouteDef[], hash: string): RouteMatch | null {
  const segs = hashSegments(hash);
  let best: { readonly match: RouteMatch; readonly fixed: number } | null = null;
  for (const route of routes) {
    const pattern = hashSegments(route.path);
    if (pattern.length !== segs.length) continue;
    const params: Record<string, string> = {};
    let fixed = 0;
    let ok = true;
    for (let i = 0; i < pattern.length && ok; i++) {
      const p = pattern[i] ?? '';
      const seg = segs[i] ?? '';
      if (p.startsWith(':')) params[p.slice(1)] = seg;
      else if (p === seg) fixed++;
      else ok = false;
    }
    if (ok && (best === null || fixed > best.fixed)) best = { match: { route, params }, fixed };
  }
  return best?.match ?? null;
}

function subscribeHash(cb: () => void): () => void {
  window.addEventListener('hashchange', cb);
  window.addEventListener('popstate', cb);
  return () => {
    window.removeEventListener('hashchange', cb);
    window.removeEventListener('popstate', cb);
  };
}

/** The current hash, normalised (re-renders on navigation, pushState through the nav service included). */
export function useHash(services: AppServices): string {
  const nav = services.nav as Partial<{ subscribe(cb: () => void): () => void }>;
  const subscribe = useCallback(
    (cb: () => void): (() => void) => {
      const offHash = subscribeHash(cb);
      const offNav = typeof nav.subscribe === 'function' ? nav.subscribe(cb) : undefined;
      return () => {
        offHash();
        offNav?.();
      };
    },
    [nav],
  );
  const get = (): string => normalizeHash(window.location.hash);
  return useSyncExternalStore(subscribe, get, get);
}

/** Routes that draw the notice row themselves (the editor, under its top bar). */
export function drawsOwnNotices(path: string): boolean {
  return path.startsWith('#/p/');
}

export function Router(props: { readonly services: AppServices }) {
  const { services } = props;
  const hash = useHash(services);
  const ui = useOptionalShell()?.ui ?? null;
  const match = matchRoute(services.registry.routes(), hash);
  if (match === null) return <NoRoute services={services} hash={hash} />;
  const Page = match.route.component;
  const page = (
    <ErrorBoundary variant="app" services={services} resetKey={hash} label={match.route.path}>
      <Page services={services} params={match.params} />
    </ErrorBoundary>
  );
  if (ui === null || drawsOwnNotices(match.route.path)) return page;
  return (
    <div className={s.routeWithNotices}>
      <Banners services={services} ui={ui} />
      <div className={s.routePage}>{page}</div>
    </div>
  );
}

function NoRoute(props: { readonly services: AppServices; readonly hash: string }) {
  const library = props.hash === '#/';
  return (
    <main className={s.noRoute}>
      <EmptyState
        icon={library ? 'LibraryBig' : 'MapPinOff'}
        title={{ key: library ? m.libraryUnavailableTitle : m.notFoundTitle }}
        body={library ? { key: m.libraryUnavailableBody } : { key: m.notFoundBody, params: { hash: props.hash } }}
        actions={
          library
            ? [{ label: { key: m.openSettings }, command: 'settings.open' }]
            : [{ label: { key: m.backToLibrary }, command: 'app.library' }]
        }
      />
    </main>
  );
}
